import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const ComplianceTrackingPanel = ({ complianceData }) => {
  const { t, isRTL } = useLanguage();

  const complianceItems = [
    {
      id: 1,
      title: t('visas.nusukSync'),
      status: complianceData?.nusukSync,
      lastSync: complianceData?.lastNusukSync,
      icon: 'RefreshCw',
      description: t('visas.nusukSyncDesc')
    },
    {
      id: 2,
      title: t('visas.regulatoryCompliance'),
      status: complianceData?.regulatoryCompliance,
      lastCheck: complianceData?.lastComplianceCheck,
      icon: 'Shield',
      description: t('visas.saudiRegulations')
    },
    {
      id: 3,
      title: t('visas.auditTrail'),
      status: complianceData?.auditTrail,
      recordCount: complianceData?.auditRecordCount,
      icon: 'FileCheck',
      description: t('visas.auditTrailDesc')
    },
    {
      id: 4,
      title: t('visas.autoArchiving'),
      status: complianceData?.autoArchiving,
      archivedCount: complianceData?.archivedVisaCount,
      icon: 'Archive',
      description: t('visas.autoArchivingDesc')
    }
  ];

  const getStatusConfig = (status) => {
    if (status === 'active' || status === 'compliant') {
      return {
        color: 'text-success',
        bgColor: 'bg-success/10',
        borderColor: 'border-success/20',
        label: t('visas.active')
      };
    }
    if (status === 'warning') {
      return {
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        label: t('visas.warning')
      };
    }
    return {
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive/20',
      label: t('visas.inactive')
    };
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-warm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Shield" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('visas.complianceTrackingTitle')}</h3>
          <p className="caption text-muted-foreground">{t('visas.saudiCompliance')}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {complianceItems?.map((item) => {
          const statusConfig = getStatusConfig(item?.status);
          
          return (
            <div 
              key={item?.id}
              className={`p-4 rounded-lg border ${statusConfig?.bgColor} ${statusConfig?.borderColor}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full ${statusConfig?.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={item?.icon} size={18} className={statusConfig?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-medium text-foreground">{item?.title}</p>
                    <span className={`text-xs font-medium ${statusConfig?.color}`}>
                      {statusConfig?.label}
                    </span>
                  </div>
                  <p className="caption text-muted-foreground mb-2">{item?.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {item?.lastSync && (
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {new Date(item.lastSync)?.toLocaleString('en-US')}
                      </span>
                    )}
                    {item?.lastCheck && (
                      <span className="flex items-center gap-1">
                        <Icon name="CheckCircle" size={12} />
                        {new Date(item.lastCheck)?.toLocaleString('en-US')}
                      </span>
                    )}
                    {item?.recordCount !== undefined && (
                      <span className="flex items-center gap-1">
                        <Icon name="Database" size={12} />
                        {item?.recordCount} {t('visas.records')}
                      </span>
                    )}
                    {item?.archivedCount !== undefined && (
                      <span className="flex items-center gap-1">
                        <Icon name="Archive" size={12} />
                        {item?.archivedCount} {t('visas.archived')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComplianceTrackingPanel;